(() => {
  'use strict';

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const body = document.body;
  const nav = document.querySelector('.site-nav');
  const navLogo = document.getElementById('navLogo');
  const menuTrigger = document.getElementById('menuTrigger');
  const sideMenu = document.getElementById('sideMenu');
  const sideOverlay = document.getElementById('sideOverlay');
  const cartTrigger = document.getElementById('cartTrigger');
  const cartPanel = document.getElementById('cartPanel');
  const cartOverlay = document.getElementById('cartOverlay');
  const cartCount = document.getElementById('cartCount');
  const cartEmpty = document.getElementById('cartEmpty');
  const cartItems = document.getElementById('cartItems');
  const cartTotal = document.getElementById('cartTotal');
  const cartFooter = document.getElementById('cartFooter');

  const locale = body.dataset.locale || document.documentElement.lang || 'en';
  const currency = body.dataset.currency || 'USD';
  const rootUrl = cartPanel?.dataset.rootUrl || window.Shopify?.routes?.root || '/';
  const cartUrl = `${rootUrl}cart.js`;
  const cartChangeUrl = `${rootUrl}cart/change.js`;
  const cartInitialState = document.getElementById('cartInitialState');
  const cartAssetImages = new Map([
    ['Reaching Star Shirt - Blue Pinstripe', cartPanel?.dataset.blueCartImage],
    ['Reaching Star Shirt - White Pinstripe', cartPanel?.dataset.whiteCartImage]
  ]);
  let cartState = null;
  let pendingAddCount = 0;

  try {
    cartState = JSON.parse(cartInitialState?.textContent || 'null');
  } catch {
    cartState = null;
  }

  const menuOpenLabel = cartPanel?.dataset.menuOpenLabel || 'Open menu';
  const menuCloseLabel = cartPanel?.dataset.menuCloseLabel || 'Close menu';
  const cartOpenLabel = cartPanel?.dataset.openLabel || 'Open cart';
  const cartCloseLabel = cartPanel?.dataset.closeLabel || 'Close cart';

  const setExpandedState = (trigger, expanded, openLabel, closeLabel) => {
    if (!trigger) return;
    trigger.classList.toggle('is-open', expanded);
    trigger.setAttribute('aria-expanded', String(expanded));
    trigger.setAttribute('aria-label', expanded ? closeLabel : openLabel);
  };

  const closeMenu = () => {
    sideMenu?.classList.remove('open');
    sideOverlay?.classList.remove('open');
    sideMenu?.setAttribute('aria-hidden', 'true');
    setExpandedState(menuTrigger, false, menuOpenLabel, menuCloseLabel);
    body.classList.remove('menu-open');
    if (!body.classList.contains('cart-open')) body.classList.remove('modal-open');
  };

  const closeCart = () => {
    cartPanel?.classList.remove('open');
    cartOverlay?.classList.remove('open');
    cartPanel?.setAttribute('aria-hidden', 'true');
    setExpandedState(cartTrigger, false, cartOpenLabel, cartCloseLabel);
    body.classList.remove('cart-open');
    if (!body.classList.contains('menu-open')) body.classList.remove('modal-open');
  };

  const openMenu = () => {
    closeCart();
    sideMenu?.classList.add('open');
    sideOverlay?.classList.add('open');
    sideMenu?.setAttribute('aria-hidden', 'false');
    setExpandedState(menuTrigger, true, menuOpenLabel, menuCloseLabel);
    body.classList.add('modal-open', 'menu-open');
  };

  const openCart = () => {
    closeMenu();
    cartPanel?.classList.add('open');
    cartOverlay?.classList.add('open');
    cartPanel?.setAttribute('aria-hidden', 'false');
    setExpandedState(cartTrigger, true, cartOpenLabel, cartCloseLabel);
    body.classList.add('modal-open', 'cart-open');
  };

  menuTrigger?.addEventListener('click', () => {
    if (sideMenu?.classList.contains('open')) closeMenu();
    else openMenu();
  });

  cartTrigger?.addEventListener('click', () => {
    if (cartPanel?.classList.contains('open')) closeCart();
    else openCart();
  });

  sideOverlay?.addEventListener('click', closeMenu);
  cartOverlay?.addEventListener('click', closeCart);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMenu();
    closeCart();
    document.querySelectorAll('.modal-overlay.open').forEach((modal) => closeModal(modal));
  });

  if (nav && navLogo) {
    const scrollEnter = 96;
    const scrollExit = 56;
    let isScrolled = window.scrollY > scrollEnter;
    let ticking = false;

    const updateNav = () => {
      const shouldBeScrolled = isScrolled
        ? window.scrollY > scrollExit
        : window.scrollY > scrollEnter;

      if (shouldBeScrolled !== isScrolled) {
        isScrolled = shouldBeScrolled;
        nav.classList.toggle('scrolled', isScrolled);
      }
      ticking = false;
    };

    nav.classList.toggle('scrolled', isScrolled);
    window.addEventListener('scroll', () => {
      if (ticking) return;
      window.requestAnimationFrame(updateNav);
      ticking = true;
    }, { passive: true });
  }

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const cartItemImage = (item) => {
    return cartAssetImages.get(item.product_title)
      || item.image
      || item.featured_image?.url
      || '';
  };

  const createCartItem = (item) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'cart-item';
    wrapper.dataset.key = item.key;

    const imageUrl = cartItemImage(item);
    if (imageUrl) {
      const imageLink = document.createElement('a');
      imageLink.href = item.url;
      imageLink.tabIndex = -1;

      const image = document.createElement('img');
      image.className = 'cart-item-image';
      image.src = imageUrl;
      image.alt = item.product_title;
      image.width = 400;
      image.height = 600;
      image.loading = 'eager';
      image.decoding = 'async';
      image.fetchPriority = 'high';
      imageLink.append(image);
      wrapper.append(imageLink);
    }

    const details = document.createElement('div');
    details.className = 'cart-item-details';

    const description = document.createElement('div');
    const name = document.createElement('p');
    name.className = 'cart-item-name';
    const nameLink = document.createElement('a');
    nameLink.href = item.url;
    nameLink.textContent = item.product_title;
    name.append(nameLink);
    description.append(name);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const size = document.createElement('p');
      size.className = 'cart-item-size';
      size.textContent = `${cartPanel?.dataset.size || 'Size'} ${item.variant_title}`;
      description.append(size);
    }

    const price = document.createElement('p');
    price.className = 'cart-item-price';
    price.textContent = formatMoney(item.final_line_price);
    description.append(price);

    const controls = document.createElement('div');
    controls.className = 'cart-item-controls';

    const decrease = document.createElement('button');
    decrease.className = 'cart-qty-btn';
    decrease.type = 'button';
    decrease.dataset.cartQuantity = String(item.quantity - 1);
    decrease.setAttribute('aria-label', cartPanel?.dataset.decrease || 'Decrease quantity');
    decrease.textContent = '\u2212';
    decrease.disabled = Boolean(item._pending);

    const quantity = document.createElement('span');
    quantity.className = 'cart-qty-number';
    quantity.textContent = String(item.quantity);

    const increase = document.createElement('button');
    increase.className = 'cart-qty-btn';
    increase.type = 'button';
    increase.dataset.cartQuantity = String(item.quantity + 1);
    increase.setAttribute('aria-label', cartPanel?.dataset.increase || 'Increase quantity');
    increase.textContent = '+';
    increase.disabled = Boolean(item._pending);

    controls.append(decrease, quantity, increase);
    details.append(description, controls);
    wrapper.append(details);
    return wrapper;
  };

  const renderCart = (cart) => {
    if (!cartItems || !cartCount || !cartTotal || !cartFooter || !cartEmpty) return;

    cartState = cart;
    cartItems.replaceChildren(...cart.items.map(createCartItem));
    cartCount.textContent = String(cart.item_count);
    cartCount.classList.toggle('visible', cart.item_count > 0);
    cartEmpty.hidden = cart.item_count > 0;
    cartFooter.classList.toggle('visible', cart.item_count > 0);
    cartTotal.textContent = formatMoney(cart.total_price);
  };

  const fetchCart = async () => {
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const refreshCart = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) cartPanel?.setAttribute('aria-busy', 'true');
      renderCart(await fetchCart());
    } catch {
      // Preserve the server-rendered cart if the request is unavailable.
    } finally {
      if (!quiet) cartPanel?.setAttribute('aria-busy', 'false');
    }
  };

  const optimisticCartWith = (detail) => {
    const variantId = Number(detail.variantId);
    const price = Number(detail.price) || 0;
    const quantityToAdd = Math.max(1, Number(detail.quantity) || 1);
    const source = cartState || { items: [], item_count: 0, total_price: 0 };
    const items = Array.from(source.items || [], (item) => ({ ...item }));
    const existing = items.find((item) => Number(item.variant_id || item.id) === variantId);

    if (existing) {
      existing.quantity = Number(existing.quantity || 0) + quantityToAdd;
      existing.final_line_price = Number(existing.final_line_price || 0) + (price * quantityToAdd);
      existing._pending = true;
    } else {
      items.unshift({
        key: `pending-${detail.requestId}`,
        id: variantId,
        variant_id: variantId,
        product_title: detail.productTitle,
        variant_title: detail.size,
        final_line_price: price * quantityToAdd,
        quantity: quantityToAdd,
        image: detail.image,
        url: detail.url,
        _pending: true
      });
    }

    return {
      ...source,
      items,
      item_count: Number(source.item_count || 0) + quantityToAdd,
      total_price: Number(source.total_price || 0) + (price * quantityToAdd)
    };
  };

  const changeCartLine = async (key, quantity) => {
    cartPanel?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      renderCart(await response.json());
    } finally {
      cartPanel?.setAttribute('aria-busy', 'false');
    }
  };

  cartItems?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-quantity]');
    if (!button) return;
    const item = button.closest('.cart-item');
    if (!item?.dataset.key) return;
    changeCartLine(item.dataset.key, Number(button.dataset.cartQuantity)).catch(refreshCart);
  });

  document.addEventListener('unxampld:cart-refresh', refreshCart);
  document.addEventListener('unxampld:cart-open', () => {
    openCart();
    refreshCart({ quiet: true });
  });
  document.addEventListener('unxampld:cart-pending-add', (event) => {
    pendingAddCount += 1;
    renderCart(optimisticCartWith(event.detail || {}));
    openCart();
  });

  const settlePendingAdd = () => {
    pendingAddCount = Math.max(0, pendingAddCount - 1);
    if (pendingAddCount === 0) refreshCart({ quiet: true });
  };

  document.addEventListener('unxampld:cart-added', settlePendingAdd);
  document.addEventListener('unxampld:cart-add-error', settlePendingAdd);

  if (!cartState) refreshCart();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart({ quiet: true });
  });

  const openModal = (modal) => {
    if (!modal) return;
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    body.classList.add('modal-open');
    modal.querySelector('[data-modal-close]')?.focus();
  };

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    if (!body.classList.contains('menu-open') && !body.classList.contains('cart-open')) {
      body.classList.remove('modal-open');
    }
  }

  document.querySelectorAll('[data-modal-open]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      openModal(document.getElementById(trigger.dataset.modalOpen));
    });
  });

  document.querySelectorAll('.modal-overlay').forEach((modal) => {
    modal.querySelector('[data-modal-close]')?.addEventListener('click', () => closeModal(modal));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal(modal);
    });
  });

  document.querySelectorAll('.single-line-textbox').forEach((field) => {
    field.addEventListener('focus', () => {
      field.dataset.placeholder = field.placeholder;
      field.placeholder = '';
    });
    field.addEventListener('blur', () => {
      if (!field.value) field.placeholder = field.dataset.placeholder || '';
    });
  });
})();
